import { CarriageMethod, StockStatus, Time } from 'models/ProductMeta';

export type GtmEventType =
  | 'add_payment_info'
  | 'add_contact_info'
  | 'add_shipping_info'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'begin_checkout'
  | 'continue_to_payment'
  | 'change_payment_info'
  | 'clear_cart'
  | 'copy_text'
  | 'ecommerce_clear'
  | 'footer_navigation'
  | 'installation_zip_code'
  | 'internal_navigation'
  | 'newsletter_modal'
  | 'newsletter_signup'
  | 'page_view'
  | 'remove_from_cart'
  | 'remove_from_wishlist'
  | 'search'
  | 'select_item'
  | 'select_promotion'
  | 'view_cart'
  | 'view_item'
  | 'view_item_list'
  | 'view_more_products'
  | 'login'
  | 'quotation_request'
  | 'quotation_request_submit'
  | 'help_center_faq'
  | 'help_center_category'
  | 'help_center_question'
  | 'voyado_cart'
  | 'customer_support_form_submit'
  | 'popular_category_nav'
  | 'edit_quantity'
  | 'view_group_addon'
  | 'view_promotion'
  | 'view_price_match_guarantee';

export type GtmAddToCartLocation = 'image_component' | 'sticky' | 'slide_in';

export type GtmNewsletterPlacement = 'footer' | 'modal' | 'checkout';

export const GTM_LIST_NAME = {
  ACCESSORY: 'accessory',
  ADDON: 'addon',
  BRAND: 'brand',
  CAMPAIGN_PAGE: 'campaign_page',
  CART: 'cart',
  CART_RECOMMENDATIONS: 'cart_recommendation',
  CHECKOUT: 'checkout',
  FOOTER: 'footer',
  LAST_VIEWED: 'last_viewed',
  OTHERS_ALSO_BOUGHT: 'others_also_bought',
  PRODUCT_LIST: 'category',
  REPORTS_GUIDES: 'reports_and_guides',
  SEARCH_PAGE: 'search_result',
  SERIES_PRODUCT: 'series_products',
  SPONSORED: 'sponsored',
  THECA_EXTERNAL: 'theca_external',
  THECA_INTERNAL: 'theca_internal',
  TOP_SELLER: 'category_bestsellers'
};

export type BaseGtmEvent = {
  event: GtmEventType;
};

/**
 * Enum for the status of the price.
 *
 * This enum is created to make it more uniform when viewing the values in the analytics. This way we get the same value
 * regardless of language/site.
 */
export enum GtmPriceStatus {
  NONE = 0,
  CAMPAIGN = 1,
  LOWERED_PRICE = 2,
  SEE_PRICE = 3
}

export type GtmSearchType = 'instant_search' | 'search_results_page';

export type GtmSearchEvent = BaseGtmEvent & {
  search_type: GtmSearchType;
  search_term: string;
};

export type GtmNavigationEvent = BaseGtmEvent & {
  link_url: string;
  title?: string;
};

export type GtmProduct = {
  item_brand: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_category: string;
  item_id: string;
  item_instock: number;
  item_name: string;
  item_price_status: GtmPriceStatus;
  item_price_tag: string;
  item_review_quantity: number;
  item_review_score: number;
  item_variant: string;
  item_web_catalog_id: string;
  price: number;
  item_delivery_time: Time;
  item_delivery_method: CarriageMethod;
  item_stock_status: StockStatus;
  item_freight_cost: number;
  item_images: number;
  item_addons_available: boolean;
};

export type GtmQuotationProduct = {
  item_name: string;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
};

export type GtmCartProduct = GtmProduct & {
  quantity: number;
  add_to_cart_location?: string;
  item_list_name?: string;
  index?: number;
};

export type GtmItemProduct = GtmProduct & {
  item_list_name: string;
  index?: number;
};

export type GtmPromotion = {
  creative_name: string;
  creative_slot: string;
  promotion_id: string;
  promotion_name: string;
  page_type?: string;
};

type Ecommerce<T> = {
  currency: string;
  items: T[];
};

export type CheckoutEcommerce = Ecommerce<GtmCartProduct> & {
  value: number;
  cart_url?: string;
  payment_method?: string;
};

export type VoyadoCartItem = {
  itemId: string;
  quantity: number;
};

export type VoyadoCart = {
  cartRef: string;
  cartUrl?: string;
  contactId?: string;
  locale: string;
  items: VoyadoCartItem[];
};

export type CartEcommerce = Ecommerce<GtmCartProduct> & {
  value: number;
};

type ItemEcommerce = Ecommerce<GtmItemProduct> & {
  itemListPageNbr?: number;
};

type FavouriteEcommerce = Ecommerce<GtmProduct> & {
  value: number;
};

type QuantityEcommerce = Ecommerce<GtmProduct> & {
  quantity: number;
  prevQuantity: number;
};

// EVENT TYPES

export type ClearEcommerceEvent = BaseGtmEvent & {
  ecommerce: null;
};

export type ViewItemEvent = BaseGtmEvent & {
  ecommerce: Ecommerce<GtmProduct>;
};

export type CartEvent = BaseGtmEvent & {
  ecommerce: CartEcommerce;
};

export type RemoveFromCartEvent = BaseGtmEvent & {
  ecommerce: Ecommerce<GtmCartProduct>;
};

export type PageViewEvent = BaseGtmEvent & {
  page_type: string;
  experiment_string: string;
};

export type CheckoutEvent = BaseGtmEvent & {
  ecommerce: CheckoutEcommerce;
};

export type ItemEvent = BaseGtmEvent & {
  ecommerce: ItemEcommerce;
};

export type PromotionEvent = BaseGtmEvent & {
  ecommerce: GtmPromotion;
};

export type FavouriteEvent = BaseGtmEvent & {
  ecommerce: FavouriteEcommerce;
};

export type NewsletterModalEvent = BaseGtmEvent & {
  interaction: string;
};

export type NewsletterEvent = BaseGtmEvent & {
  signup_placement: string;
};

export type CopyProductTitleEvent = BaseGtmEvent & {
  copied_item_name: string;
  copied_item_id: string;
};

export type ZipCodeEvent = BaseGtmEvent & {
  zip_code_status: 'installation_available' | 'installation_not_available';
};

export type QuotationRequestEvent = BaseGtmEvent & {
  quotation: {
    item: GtmQuotationProduct;
  };
};

export type HelpCenterFAQEvent = BaseGtmEvent & {
  faq_question: string;
};

export type HelpCenterSelectedCategoryEvent = BaseGtmEvent & {
  category: string;
};

export type HelpCenterSelectedQuestionEvent = BaseGtmEvent & {
  category: string;
  question: string;
};

export type CustomerSupportFormSendEvent = BaseGtmEvent & {
  form: string;
  page: string;
};

export type VoyadoCartEvent = BaseGtmEvent & {
  cart: VoyadoCart;
};

export type QuantityEvent = BaseGtmEvent & {
  ecommerce: QuantityEcommerce;
};

export type GroupAddonEvent = BaseGtmEvent & {
  viewed_product: string;
  group_addons: string[];
};
